import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Deduction } from '../../types';
import Button from './Button';
import TextArea from './TextArea';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { invoiceHiresService } from '../../services';
import { DeductionHeaderSection, DeductionDetailsSection } from './DeductionFormSections';

interface DeductionFormProps {
  deduction?: Deduction;
  onSubmit: (data: Partial<Deduction>) => void;
  onCancel: () => void;
}

const DeductionForm: React.FC<DeductionFormProps> = ({ deduction, onSubmit, onCancel }) => {
  const { data: currencies } = useCurrencies();
  const { data: clients } = useClients();
  const { data: branches } = useBranches();
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currencyOptions = useMemo(() =>
    (currencies || [])
      .filter((c: any) => c.isActive !== false)
      .map((c: any) => ({ value: c.documentId || c.id || '', label: c.code || '' })),
    [currencies]);

  const clientOptions = useMemo(() =>
    (clients || []).map((c: any) => ({ value: c.documentId || c.id, label: c.name })),
    [clients]);

  const vesselOptions = useMemo(() =>
    (branches || [])
      .filter((b: any) => b.type === 'vessel')
      .map((v: any) => ({ value: v.documentId || v.id, label: v.name })),
    [branches]);

  const {
    register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue
  } = useForm<Deduction>({
    defaultValues: deduction ? {
      ...deduction,
      deductionDate: formatDateForInput(deduction.deductionDate)
    } : {
      deductionNumber: '', invoiceId: '', clientId: '', vesselId: '',
      amount: 0, percentage: 0, type: 'percentage', reason: '',
      currency: '', status: 'active',
      deductionDate: new Date().toISOString().split('T')[0], notes: ''
    }
  });

  const deductionType = watch('type');
  const invoiceIdValue = watch('invoiceId');
  const watchedCurrency = watch('currency');
  const watchedAmount = watch('amount');
  const watchedPercentage = watch('percentage');
  const watchedRate = watch('exchangeRate');

  const deductionCurrencyCode = useMemo(
    () => resolveCurrencyCode(watchedCurrency, currencies as any[]),
    [watchedCurrency, currencies]);
  const invoiceCurrencyCode = useMemo(
    () => (selectedInvoice ? resolveCurrencyCode(selectedInvoice.currency, currencies as any[]) : ''),
    [selectedInvoice, currencies]);
  const invoiceTotal = Number(selectedInvoice?.originalTotal ?? selectedInvoice?.netTotal ?? 0);

  // Server-side searchable invoice options (invoice-hire collection)
  const fetchInvoiceOptions = useCallback(async (query: string, page: number) => {
    return invoiceHiresService.getAll({
      pagination: { page, pageSize: 20 },
      sort: 'createdAt:DESC',
      ...(query ? { q: query } : {}),
    });
  }, []);

  // Selecting an invoice auto-fills client, vessel and currency
  const handleInvoiceSelect = useCallback((invoice: any | null) => {
    setSelectedInvoice(invoice);
    if (!invoice) return;
    const clientId = invoice.customer?.documentId || invoice.customer?.id || invoice.customerId;
    if (clientId) setValue('clientId', String(clientId));
    const vesselId = invoice.vessel?.documentId || invoice.vessel?.id || invoice.vesselId;
    if (vesselId) setValue('vesselId', String(vesselId));
    const code = resolveCurrencyCode(invoice.currency, currencies as any[]);
    const match = (currencies || []).find((c: any) => c.code === code);
    if (match) setValue('currency', match.documentId || match.id);
  }, [setValue, currencies]);

  useEffect(() => {
    if (!deduction) {
      const timestamp = Date.now().toString().slice(-6);
      setValue('deductionNumber', `DED-${timestamp}`);
    }
  }, [deduction, setValue]);

  const onFormSubmit = (data: Deduction) => {
    setSubmitError(null);
    if (!data.invoiceId) {
      setSubmitError('Please select the invoice this deduction applies to.');
      return;
    }
    const isPercentage = data.type === 'percentage';
    const mismatch = !isPercentage && !!invoiceCurrencyCode &&
      !!deductionCurrencyCode && invoiceCurrencyCode !== deductionCurrencyCode;
    if (mismatch && (!data.exchangeRate || data.exchangeRate <= 0)) {
      const rateCurrency = deductionCurrencyCode !== 'EGP' ? deductionCurrencyCode : invoiceCurrencyCode;
      setSubmitError(`Exchange rate (${rateCurrency} to EGP) is required — the deduction currency differs from the invoice currency.`);
      return;
    }
    // Deduction value expressed in the invoice's currency; the backend uses it
    // to reduce the invoice remaining amount
    const amount = Number(data.amount) || 0;
    const rate = Number(data.exchangeRate) || 0;
    const invoiceAppliedAmount = isPercentage
      ? (invoiceTotal * (Number(data.percentage) || 0)) / 100
      : !mismatch ? amount
      : deductionCurrencyCode === 'EGP' ? amount / rate
      : invoiceCurrencyCode === 'EGP' ? amount * rate
      : amount;
    onSubmit({
      ...data,
      deductionDate: formatDateForAPI(data.deductionDate),
      exchangeRate: data.exchangeRate || undefined,
      invoiceAppliedAmount: selectedInvoice ? Number(invoiceAppliedAmount.toFixed(2)) : data.invoiceAppliedAmount,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {deduction ? 'Edit Deduction' : 'Create Invoice Deduction'}
          </h1>
          <p className="text-gray-600">
            {deduction ? 'Update deduction details' : 'Create a new invoice deduction'}
          </p>
        </div>
      </div>

      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <DeductionHeaderSection
          register={register}
          errors={errors}
          clientOptions={clientOptions}
          vesselOptions={vesselOptions}
          invoiceValue={invoiceIdValue || ''}
          onInvoiceChange={(value) => setValue('invoiceId', value)}
          onInvoiceSelect={handleInvoiceSelect}
          fetchInvoiceOptions={fetchInvoiceOptions}
          selectedInvoice={selectedInvoice}
        />

        <DeductionDetailsSection
          register={register}
          errors={errors}
          deductionType={deductionType}
          currencyOptions={currencyOptions}
          invoiceCurrencyCode={invoiceCurrencyCode}
          deductionCurrencyCode={deductionCurrencyCode}
          amountValue={Number(watchedAmount) || 0}
          percentageValue={Number(watchedPercentage) || 0}
          exchangeRateValue={Number(watchedRate) || 0}
          invoiceTotal={invoiceTotal}
        />

        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Additional Information</h2>
          </div>
          <div className="card-body">
            <TextArea
              label="Notes"
              {...register('notes')}
              rows={4}
              placeholder="Additional notes about this deduction..."
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Button type="button" onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : deduction ? 'Update Deduction' : 'Create Deduction'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeductionForm;
